/**
 * `<EditorChatMount>` — mounts the `/editor` and `/chat` surfaces into the `ShellMount`
 * contract (editor-chat.md §2.1 / §3.1; ADR 007; the frontend's `ShellMount.ts`).
 *
 * This is the editor-chat workstream's single entry point into the frontend shell. The
 * frontend owns the *shell* — the nav sidebar, the route outlet, the command palette, the
 * status bar; Agent 9 owns the `/editor` and `/chat` route *elements*. This component mounts
 * those elements into the shell **exactly the way `seedCoreSurfaces` mounts the core seven** —
 * through the same `ShellMount` registry, no privileged path (`ShellMount.ts`: "the contract
 * is symmetric, which is what makes it a real contract and not a backdoor").
 *
 * It registers, on mount, and unregisters on unmount (via the thunks `register*` returns):
 *  - `registerRoute({ pattern, element })` — the `/editor/:noteId?` and `/chat` route elements;
 *  - `registerNavItem(...)` — the Editor / Chat nav-sidebar entries, at orders **70 / 71**
 *    (the core seven occupy 0..69; `ShellMount.ts`: "Agent 9 uses 70+");
 *  - `registerCommand(...)` — the "Go to Editor" / "Go to Chat" command-palette entries, plus
 *    a "New chat" command;
 *  - `statusBarSlot('right', ...)` — the chat token-cost indicator in the global status bar
 *    (`ShellMount.ts` names exactly this as Agent 9's status-bar slot).
 *
 * `<AppShell>` renders this once, inside its `ShellMountProvider` and the router context, so
 * `useShellMount()` and `useNavigate()` resolve. It renders nothing itself (`null`) — it is a
 * pure registration effect.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShellMount } from '@/shell/ShellMountContext';
import { ROUTES, ROUTE_PATTERNS } from '@/routes';
import { EditorSurface } from './EditorSurface';
import { ChatSurface } from '@/chat/ChatSurface';

/**
 * Mount the editor-chat surfaces into the `ShellMount`. Registers on mount, tears down on
 * unmount. Renders `null` — it is a registration effect, not UI.
 */
export function EditorChatMount(): null {
  const mount = useShellMount();
  const navigate = useNavigate();

  useEffect(() => {
    // Collect every unregister thunk so unmount is a clean, complete teardown — the same
    // pattern `seedCoreSurfaces` uses.
    const unregisters: Array<() => void> = [];

    // --- /editor — the CM6 authoring surface --------------------------------------------
    unregisters.push(
      mount.registerRoute({
        pattern: ROUTE_PATTERNS.editor, // '/editor/:noteId?'
        element: <EditorSurface />,
      }),
    );
    unregisters.push(
      mount.registerNavItem({
        id: 'editor',
        label: 'Editor',
        route: ROUTES.editor,
        // UX-reconciliation IA contraction (2026-05-16) — the "main loop" is
        // Editor (0) · Search (10) · Chat (20). Editor leads the primary group;
        // Search sits in the middle (registered by surfaceRegistry); Chat closes
        // the row. The legacy "Agent 9 uses 70+" comment in ShellMount.ts is
        // superseded by the explicit cross-module ordering documented here.
        order: 0,
        group: 'primary',
        iconName: 'edit',
        kbd: 'e',
        glyph: 'E',
      }),
    );
    unregisters.push(
      mount.registerCommand({
        id: 'navigate.editor',
        label: 'Go to Editor',
        group: 'Navigate',
        keywords: ['editor', 'write', 'note', 'edit'],
        run: () => navigate(ROUTES.editor),
      }),
    );

    // --- /chat — the streaming SSE chat surface -----------------------------------------
    unregisters.push(
      mount.registerRoute({
        pattern: ROUTE_PATTERNS.chat, // '/chat'
        element: <ChatSurface />,
      }),
    );
    unregisters.push(
      mount.registerNavItem({
        id: 'chat',
        label: 'Chat',
        route: ROUTES.chat,
        // UX-reconciliation IA contraction (2026-05-16) — Chat is the third leg of
        // the main loop (Editor · Search · Chat). Moved from `system` to `primary`
        // at order 20, after Search (order 10) registered by surfaceRegistry.
        order: 20,
        group: 'primary',
        iconName: 'chat',
        kbd: 'c',
        glyph: 'C',
      }),
    );
    unregisters.push(
      mount.registerCommand({
        id: 'navigate.chat',
        label: 'Go to Chat',
        group: 'Navigate',
        keywords: ['chat', 'assistant', 'ask'],
        run: () => navigate(ROUTES.chat),
      }),
    );
    unregisters.push(
      mount.registerCommand({
        id: 'chat.new',
        label: 'New chat',
        group: 'Chat',
        keywords: ['chat', 'new', 'conversation'],
        // Navigating to /chat with no session is a fresh chat — the surface's default state.
        run: () => navigate(ROUTES.chat),
      }),
    );

    // (The chat token-cost indicator status-bar slot was removed 2026-05-21 with the rest
    // of the cost telemetry.)

    // Unmount: run every unregister thunk — a clean, complete teardown.
    return () => {
      for (const unregister of unregisters) unregister();
    };
  }, [mount, navigate]);

  return null;
}
